// // ─── CartelaSelectionModal.jsx ────────────────────────────────────────────────

import { useState, useEffect, useCallback } from "react";
import {
  ChevronLeft,
  X,
  Wallet,
  Zap,
  Trophy,
  Clock,
  LayoutGrid,
  AlignJustify,
} from "lucide-react";
import { theme, injectTheme } from "../../assets/theme";

// ─── Inject keyframes (once) ──────────────────────────────────────────────────

function injectCartelaStyles() {
  if (document.getElementById("cartela-modal-styles")) return;
  const s = document.createElement("style");
  s.id = "cartela-modal-styles";
  s.textContent = `
    @keyframes slideInFull {
      from { transform: translateX(100%); opacity: 0; }
      to   { transform: translateX(0);    opacity: 1; }
    }
    @keyframes fadeInScale {
      from { transform: scale(0.88); opacity: 0; }
      to   { transform: scale(1);    opacity: 1; }
    }
    @keyframes timerPulse {
      0%, 100% { opacity: 1; }
      50%       { opacity: 0.55; }
    }
    @keyframes countdownGlow {
      0%, 100% { box-shadow: 0 0 12px rgba(249,115,22,0.40); }
      50%       { box-shadow: 0 0 28px rgba(249,115,22,0.80); }
    }
    .cartela-cell {
      transition: all 0.15s ease;
    }
    .cartela-cell:hover:not(.taken) {
      transform: scale(1.12);
      z-index: 2;
    }
    /* Hide scrollbar but keep scrollability */
    .grid-scroll::-webkit-scrollbar { display: none; }
    .grid-scroll { -ms-overflow-style: none; scrollbar-width: none; }
  `;
  document.head.appendChild(s);
}

// ─── Server-synced countdown hook ────────────────────────────────────────────

function useServerCountdown(endsAt) {
  const [remaining, setRemaining] = useState(0);
  useEffect(() => {
    if (!endsAt) {
      setRemaining(0);
      return;
    }
    const tick = () =>
      setRemaining(Math.max(0, Math.round((endsAt - Date.now()) / 1000)));
    tick();
    const id = setInterval(tick, 250);
    return () => clearInterval(id);
  }, [endsAt]);
  const mm = String(Math.floor(remaining / 60)).padStart(2, "0");
  const ss = String(remaining % 60).padStart(2, "0");
  return { display: `${mm}:${ss}`, urgent: remaining <= 10, remaining };
}

// ─── StatPill ─────────────────────────────────────────────────────────────────

function StatPill({ icon: Icon, iconColor, label, value }) {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "3px",
        background: "rgba(255,255,255,0.04)",
        border: `1px solid ${theme.color.borderCard}`,
        borderRadius: theme.radius.md,
        padding: "8px 14px",
        flex: 1,
        minWidth: 0,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
        <Icon size={11} color={iconColor} />
        <span
          style={{
            fontSize: "9px",
            fontFamily: theme.font.bodyFamily,
            fontWeight: theme.font.weightSemi,
            color: theme.color.textDim,
            textTransform: "uppercase",
            letterSpacing: "0.14em",
          }}
        >
          {label}
        </span>
      </div>
      <span
        style={{
          fontFamily: theme.font.family,
          fontWeight: theme.font.weightBlack,
          fontSize: "15px",
          color: iconColor,
          fontVariantNumeric: "tabular-nums",
          lineHeight: 1,
        }}
      >
        {value}
      </span>
    </div>
  );
}

// ─── CartelaCell ──────────────────────────────────────────────────────────────

function CartelaCell({ card, selected, onToggle, atMax }) {
  const isSelected = selected.has(card.id);
  // "locked out" = max reached and this cell is NOT one of the chosen ones
  const lockedOut = atMax && !isSelected && !card.taken;

  // Colour logic
  let bg, border, color, cursor;
  if (card.taken) {
    bg = "rgba(239,68,68,0.10)";
    border = "rgba(239,68,68,0.25)";
    color = "rgba(239,68,68,0.50)";
    cursor = "not-allowed";
  } else if (isSelected) {
    bg = `linear-gradient(135deg, ${theme.color.playFrom}, ${theme.color.playTo})`;
    border = theme.color.accent;
    color = "#ffffff";
    cursor = "pointer";
  } else if (lockedOut) {
    bg = "rgba(255,255,255,0.02)";
    border = "rgba(255,255,255,0.06)";
    color = "rgba(255,255,255,0.18)";
    cursor = "not-allowed";
  } else {
    bg = "rgba(255,255,255,0.04)";
    border = theme.color.borderCard;
    color = theme.color.textMuted;
    cursor = "pointer";
  }

  return (
    <button
      className="cartela-cell"
      onClick={() => !card.taken && onToggle(card.id)}
      aria-label={`Cartela ${card.number}${card.taken ? " (taken)" : isSelected ? " (selected)" : ""}`}
      style={{
        width: "100%",
        aspectRatio: "1",
        borderRadius: "8px",
        background: bg,
        border: `1px solid ${border}`,
        color,
        cursor,
        fontFamily: theme.font.family,
        fontWeight: theme.font.weightBlack,
        fontSize: "13px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        boxShadow: isSelected ? `0 0 10px rgba(249,115,22,0.38)` : "none",
        outline: "none",
      }}
    >
      {card.number}
    </button>
  );
}

// ─── Legend dot ───────────────────────────────────────────────────────────────

function LegendDot({ color, label }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
      <span
        style={{
          width: "8px",
          height: "8px",
          borderRadius: theme.radius.full,
          background: color,
          flexShrink: 0,
        }}
      />
      <span
        style={{
          fontSize: "10px",
          fontFamily: theme.font.bodyFamily,
          fontWeight: theme.font.weightMed,
          color: theme.color.textDim,
        }}
      >
        {label}
      </span>
    </div>
  );
}

// ─── CartelaSelectionModal ────────────────────────────────────────────────────

const MAX_CARDS = 2;

export default function CartelaSelectionModal({
  open,
  wallet,
  stake,
  playerCount,
  potSize,
  cartelas = [],
  stakeUnit = "BIRR",
  takenCartelaIds = [],
  myCartelaIds = [],
  estimatedPot,
  endsAt,
  onSelect,
  onDeselect,
  onBack,
  onClose,
}) {
  const [denseView, setDenseView] = useState(false);
  const [visible, setVisible] = useState(false);
  const [animating, setAnimating] = useState(false);
  const selected = new Set(myCartelaIds);
  const CARDS = cartelas.map((c) => ({
    id: c.id,
    number: String(c.card_number).padStart(2, "0"),
    taken:
      c.is_taken || (takenCartelaIds.includes(c.id) && !selected.has(c.id)),
  }));
  const timer = useServerCountdown(endsAt);

  useEffect(() => {
    injectTheme();
    injectCartelaStyles();
  }, []);

  useEffect(() => {
    if (open) {
      setVisible(true);
      requestAnimationFrame(() => setAnimating(true));
    } else {
      setAnimating(false);
      const t = setTimeout(() => setVisible(false), 340);
      return () => clearTimeout(t);
    }
  }, [open]);

  const handleKey = useCallback(
    (e) => {
      if (e.key === "Escape") onClose?.();
    },
    [onClose],
  );

  useEffect(() => {
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [handleKey]);

  const toggleCard = (id) => {
    if (selected.has(id)) onDeselect?.(id);
    else if (selected.size < MAX_CARDS) onSelect?.(id);
  };

  const totalFee = selected.size * parseFloat(stake.replace(",", ""));

  if (!visible) return null;

  // Grid columns: 7 (normal) or 9 (dense)
  const cols = denseView ? 9 : 7;

  return (
    /* Full viewport overlay */
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 12000,
        display: "flex",
        alignItems: "stretch",
        justifyContent: "center",
        background: animating ? "rgba(0,0,0,0.88)" : "rgba(0,0,0,0)",
        backdropFilter: animating ? "blur(10px)" : "blur(0px)",
        transition: "background 0.32s ease, backdrop-filter 0.32s ease",
      }}
    >
      {/* ── Full-screen panel ─────────────────────────────── */}
      <div
        style={{
          width: "100%",
          maxWidth: "430px",
          background: theme.color.bgSurface,
          display: "flex",
          flexDirection: "column",
          transform: animating ? "translateX(0)" : "translateX(100%)",
          transition: "transform 0.36s cubic-bezier(0.32,0.72,0,1)",
          boxShadow: "-20px 0 80px rgba(0,0,0,0.90)",
          overflow: "hidden",
        }}
      >
        {/* ── Top bar: back + title + close ────────────────── */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "10px 20px 12px",
          }}
        >
          <button
            onClick={onBack}
            aria-label="Go back"
            style={{
              width: "36px",
              height: "36px",
              borderRadius: theme.radius.md,
              background: "rgba(255,255,255,0.05)",
              border: `1px solid ${theme.color.borderCard}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              transition: theme.transition.base,
              flexShrink: 0,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "rgba(255,255,255,0.10)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "rgba(255,255,255,0.05)";
            }}
          >
            <ChevronLeft size={18} color={theme.color.textMuted} />
          </button>

          <h1
            style={{
              fontFamily: theme.font.family,
              fontWeight: theme.font.weightBlack,
              fontSize: "18px",
              color: theme.color.textPrimary,
              letterSpacing: "0.06em",
              textTransform: "uppercase",
            }}
          >
            Select Cartela
          </h1>

          <button
            onClick={onClose}
            aria-label="Close"
            style={{
              width: "36px",
              height: "36px",
              borderRadius: theme.radius.md,
              background: "rgba(255,255,255,0.05)",
              border: `1px solid ${theme.color.borderCard}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              transition: theme.transition.base,
              flexShrink: 0,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "rgba(255,255,255,0.10)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "rgba(255,255,255,0.05)";
            }}
          >
            <X size={16} color={theme.color.textMuted} />
          </button>
        </div>

        {/* ── Stat pills row ───────────────────────────────── */}
        <div style={{ display: "flex", gap: "8px", padding: "0 20px 14px" }}>
          <StatPill
            icon={Wallet}
            iconColor="#fff"
            label="Wallet"
            value={wallet}
          />
          <StatPill
            icon={Zap}
            iconColor={theme.color.accentLight}
            label="Stake"
            value={stake}
          />
          <StatPill
            icon={Trophy}
            iconColor="#a78bfa"
            label="Pot Size"
            value={estimatedPot}
          />
        </div>

        {/* ── Countdown timer ──────────────────────────────── */}
        <div style={{ padding: "0 20px 14px" }}>
          <div
            style={{
              borderRadius: theme.radius.lg,
              background: timer.urgent
                ? "rgba(239,68,68,0.12)"
                : "rgba(249,115,22,0.10)",
              border: `1px solid ${
                timer.urgent ? "rgba(239,68,68,0.35)" : theme.color.accentRing
              }`,
              padding: "12px 16px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              animation: "countdownGlow 2s ease-in-out infinite",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <div
                style={{
                  width: "28px",
                  height: "28px",
                  borderRadius: theme.radius.full,
                  background: timer.urgent
                    ? "rgba(239,68,68,0.20)"
                    : theme.color.accentMuted,
                  border: `1px solid ${
                    timer.urgent
                      ? "rgba(239,68,68,0.40)"
                      : theme.color.accentRing
                  }`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Clock
                  size={14}
                  color={timer.urgent ? "#ef4444" : theme.color.accentLight}
                />
              </div>
              <span
                style={{
                  fontFamily: theme.font.bodyFamily,
                  fontWeight: theme.font.weightSemi,
                  fontSize: "11px",
                  color: theme.color.textDim,
                  textTransform: "uppercase",
                  letterSpacing: "0.14em",
                }}
              >
                Selection Ends In
              </span>
            </div>

            <span
              style={{
                fontFamily: theme.font.family,
                fontWeight: theme.font.weightBlack,
                fontSize: "28px",
                color: timer.urgent ? "#ef4444" : theme.color.textPrimary,
                fontVariantNumeric: "tabular-nums",
                letterSpacing: "0.04em",
                lineHeight: 1,
                animation: timer.urgent
                  ? "timerPulse 0.8s ease infinite"
                  : "none",
                textShadow: timer.urgent
                  ? "0 0 16px rgba(239,68,68,0.70)"
                  : `0 0 16px rgba(249,115,22,0.50)`,
              }}
            >
              {timer.display}
            </span>
          </div>
        </div>

        {/* ── Grid header: label + total + view toggle ─────── */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "0 20px 10px",
          }}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: "1px" }}>
            <span
              style={{
                fontFamily: theme.font.family,
                fontWeight: theme.font.weightBlack,
                fontSize: "14px",
                color: theme.color.textPrimary,
                textTransform: "uppercase",
                letterSpacing: "0.06em",
              }}
            >
              Available Cartelas
            </span>
            <span
              style={{
                fontFamily: theme.font.bodyFamily,
                fontWeight: theme.font.weightMed,
                fontSize: "10px",
                color: theme.color.textDim,
              }}
            >
              {/* {TOTAL_CARDS - TAKEN_IDS.size} available · {TAKEN_IDS.size} taken */}
              {CARDS.filter((c) => !c.taken).length} available ·{" "}
              {CARDS.filter((c) => c.taken).length} taken
            </span>
          </div>

          {/* View toggle: normal / dense */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "4px",
              background: "rgba(255,255,255,0.04)",
              border: `1px solid ${theme.color.borderCard}`,
              borderRadius: theme.radius.md,
              padding: "3px",
            }}
          >
            {[
              { Icon: LayoutGrid, id: false, label: "Grid" },
              { Icon: AlignJustify, id: true, label: "Dense" },
            ].map(({ Icon, id, label }) => (
              <button
                key={label}
                onClick={() => setDenseView(id)}
                aria-label={label}
                title={`${label} view`}
                style={{
                  width: "28px",
                  height: "28px",
                  borderRadius: "8px",
                  background:
                    denseView === id ? theme.color.accent : "transparent",
                  border: "none",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  transition: theme.transition.base,
                  boxShadow:
                    denseView === id
                      ? `0 0 8px ${theme.color.accentGlow}`
                      : "none",
                }}
              >
                <Icon
                  size={13}
                  color={denseView === id ? "#fff" : theme.color.textDim}
                />
              </button>
            ))}
          </div>
        </div>

        {/* ── Sub-header: cartela id grid label + dense selection label ── */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "0 20px 8px",
          }}
        >
          <span
            style={{
              fontSize: "9px",
              fontFamily: theme.font.bodyFamily,
              fontWeight: theme.font.weightSemi,
              color: theme.color.textDim,
              textTransform: "uppercase",
              letterSpacing: "0.16em",
            }}
          >
            Cartela ID Grid
          </span>
          <span
            style={{
              fontSize: "9px",
              fontFamily: theme.font.bodyFamily,
              fontWeight: theme.font.weightSemi,
              color: theme.color.textDim,
              textTransform: "uppercase",
              letterSpacing: "0.16em",
            }}
          >
            Dense Selection
          </span>
        </div>

        {/* ── Scrollable grid ──────────────────────────────── */}
        <div
          className="grid-scroll"
          style={{
            flex: 1,
            overflowY: "auto",
            padding: "0 20px 12px",
          }}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: `repeat(${cols}, 1fr)`,
              gap: denseView ? "5px" : "7px",
            }}
          >
            {CARDS.map((card) => (
              <CartelaCell
                key={card.id}
                card={card}
                selected={selected}
                onToggle={toggleCard}
                atMax={selected.size >= MAX_CARDS}
              />
            ))}
          </div>

          {/* Legend */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "16px",
              padding: "14px 0 4px",
              flexWrap: "wrap",
            }}
          >
            <LegendDot color={theme.color.accent} label="Selected" />
            <LegendDot color={theme.color.textDim} label="Available" />
            <LegendDot color="rgba(239,68,68,0.55)" label="Taken" />
          </div>
        </div>

        {/* ── Preview & confirm footer ─────────────────────── */}
        <div
          style={{
            borderTop: `1px solid ${theme.color.border}`,
            background: theme.color.bgNav,
            backdropFilter: "blur(12px)",
            padding: "16px 20px 28px",
            flexShrink: 0,
          }}
        >
          {/* Preview details */}
          <p
            style={{
              fontFamily: theme.font.family,
              fontWeight: theme.font.weightBlack,
              fontSize: "11px",
              color: theme.color.textDim,
              textTransform: "uppercase",
              letterSpacing: "0.16em",
              marginBottom: "10px",
            }}
          >
            Preview Details
          </p>

          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "8px",
              marginBottom: "16px",
            }}
          >
            {/* Selected cards */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <span
                style={{
                  fontFamily: theme.font.bodyFamily,
                  fontWeight: theme.font.weightMed,
                  fontSize: "13px",
                  color: theme.color.textMuted,
                }}
              >
                Selected Cards
              </span>
              <span
                style={{
                  fontFamily: theme.font.family,
                  fontWeight: theme.font.weightBlack,
                  fontSize: "16px",
                  color:
                    selected.size > 0
                      ? theme.color.accentLight
                      : theme.color.textDim,
                  transition: theme.transition.base,
                }}
              >
                {selected.size}
              </span>
            </div>

            {/* Total entry fee */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <span
                style={{
                  fontFamily: theme.font.bodyFamily,
                  fontWeight: theme.font.weightMed,
                  fontSize: "13px",
                  color: theme.color.textMuted,
                }}
              >
                Total Entry Fee
              </span>
              <span
                style={{
                  fontFamily: theme.font.family,
                  fontWeight: theme.font.weightBlack,
                  fontSize: "16px",
                  color:
                    selected.size > 0
                      ? theme.color.textPrimary
                      : theme.color.textDim,
                  fontVariantNumeric: "tabular-nums",
                  transition: theme.transition.base,
                }}
              >
                {selected.size > 0
                  ? `${(selected.size * parseFloat(stake.replace(",", ""))).toLocaleString()} ${stakeUnit}`
                  : "—"}
              </span>
            </div>
          </div>

          {/* Status — no manual confirm button; game auto-starts at 0 */}
          <div
            style={{
              width: "100%",
              padding: "14px 16px",
              borderRadius: theme.radius.lg,
              background:
                selected.size > 0
                  ? "rgba(249,115,22,0.08)"
                  : "rgba(255,255,255,0.04)",
              border: `1px solid ${
                selected.size > 0
                  ? theme.color.accentRing
                  : theme.color.borderCard
              }`,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "12px",
            }}
          >
            <div
              style={{ display: "flex", flexDirection: "column", gap: "3px" }}
            >
              <span
                style={{
                  fontFamily: theme.font.bodyFamily,
                  fontWeight: 700,
                  fontSize: "12px",
                  color:
                    selected.size > 0
                      ? theme.color.accentLight
                      : theme.color.textDim,
                }}
              >
                {selected.size === 0
                  ? "No cartela selected — random card will be assigned"
                  : selected.size === MAX_CARDS
                    ? `${selected.size} cards selected (max reached)`
                    : `${selected.size} card${selected.size > 1 ? "s" : ""} selected`}
              </span>
              <span
                style={{
                  fontFamily: theme.font.bodyFamily,
                  fontSize: "10px",
                  color: theme.color.textDim,
                }}
              >
                Game starts automatically when timer hits 00:00
              </span>
            </div>
            {/* Small pulsing countdown echo */}
            <span
              style={{
                fontFamily: theme.font.family,
                fontWeight: 900,
                fontSize: "18px",
                color: timer.urgent ? "#ef4444" : theme.color.accentLight,
                fontVariantNumeric: "tabular-nums",
                flexShrink: 0,
                animation: timer.urgent
                  ? "timerPulse 0.8s ease infinite"
                  : "none",
              }}
            >
              {timer.display}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
